import { GameVars } from "../GameVars";
import { AudioManager } from "../AudioManager";

export class RavalProgressBar extends Phaser.GameObjects.Container {

    private backgroundImage: Phaser.GameObjects.Image;

    private backgroundGraphic: Phaser.GameObjects.Graphics;
    private progressGraphic: Phaser.GameObjects.Graphics;

    private progressAmmount: number;
    private barWidth: number;
    private barHeight: number;

    constructor(scene: Phaser.Scene, x: number = 0, y: number = 0, width: number = 0, height: number = 0, backgroundColor: Phaser.Display.Color = new Phaser.Display.Color(0, 0, 0), foregroundColor: Phaser.Display.Color = new Phaser.Display.Color(255, 255, 255), backgroundImagePath: string, currentProgress: number = 0.5, centered: boolean = false) {

        super(scene, x, y);

        // Set Data 
        this.progressAmmount = currentProgress;
        this.barWidth = width;
        this.barHeight = height;

        // Create background image 
        this.backgroundImage = new Phaser.GameObjects.Image(scene, 0, 0, "texture_atlas_1", backgroundImagePath);
        this.backgroundImage.setOrigin(.5);
        this.backgroundImage.setDisplaySize(this.barWidth + 10, this.barHeight + 10);
        this.add(this.backgroundImage);

        // Create background graphics 
        this.backgroundGraphic = new Phaser.GameObjects.Graphics(scene);
        this.backgroundGraphic.fillStyle(backgroundColor.color, 1.0);
        this.backgroundGraphic.fillRect(0, 0, width, height);
        this.add(this.backgroundGraphic);

        // Create progress graphics 
        this.progressGraphic = new Phaser.GameObjects.Graphics(scene);
        this.progressGraphic.fillStyle(foregroundColor.color, 1.0);
        this.progressGraphic.fillRect(0, 0, width * this.progressAmmount, height);
        this.add(this.progressGraphic);

        if (centered) {
            this.progressGraphic.x -= width / 2;
            this.progressGraphic.y -= height / 2;

            this.backgroundGraphic.x -= width / 2;
            this.backgroundGraphic.y -= height / 2;
        }
    }

    public interpolateColorBackground(color: Phaser.Display.Color) {
        this.backgroundGraphic.fillGradientStyle(0, 0, color.color, color.color, 0.5);
        this.backgroundGraphic.fillRect(this.backgroundGraphic.defaultFillColor, this.backgroundGraphic.defaultFillColor, this.barWidth, this.barHeight);
    }

    public interpolateColorProgress(color: Phaser.Display.Color) {
        this.progressGraphic.fillGradientStyle(this.progressGraphic.defaultFillColor, this.progressGraphic.defaultFillColor, color.color, color.color, 1);
        this.backgroundGraphic.fillRect(0, 0, this.barWidth * this.progressAmmount, this.barHeight);
    }


    public setBackgroundImage(imagestr: string) {
        this.backgroundImage.setTexture("texture_atlas_1", imagestr);
    }

    public setProgress(newProgress: number): void {

        this.progressAmmount = newProgress;
        this.progressGraphic.fillRect(0, 0, this.barWidth * this.progressAmmount, this.barHeight);
    }

    public addProgress(newProgressInc: number): void {

        this.progressAmmount += newProgressInc;
        this.progressGraphic.fillRect(0, 0, this.barWidth * this.progressAmmount, this.barHeight);
    }

    public setBackgroundColor(newColor: Phaser.Display.Color): void {

        this.backgroundGraphic.fillStyle(newColor.color, 1.0);
        this.backgroundGraphic.fillRect(0, 0, this.barWidth, this.barHeight);
    }

    public setProgressColor(newColor: Phaser.Display.Color): void {

        this.progressGraphic.fillStyle(newColor.color, 1.0);
        this.progressGraphic.fillRect(0, 0, this.barWidth * this.progressAmmount, this.barHeight);
    }
}

export class GeneralUtils {

    // https://www.compuphase.com/cmetric.htm
    public static colourDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {

        const rmean = (r1 + r2) / 2;
        const r = r1 - r2;
        const g = g1 - g2;
        const b = b1 - b2;

        return Math.sqrt((((512 + rmean) * r * r) >> 8) + 4 * g * g + (((767 - rmean) * b * b) >> 8));
    }

    public static getAsPaddedNumber(value: number, padLength: number) {

        const valueAsString = value.toString();

        const diff = padLength - valueAsString.length;

        if (diff > 0) {
            let appendValue = "";
            for (let i = 0; i < diff; i++) {
                appendValue += "0";
            }
            return appendValue + valueAsString;
        } else if (diff < 0) {
            throw "Error: Cannot pad number larger than padLength.";
        } else {
            return valueAsString;
        }
    }
}

export class RavalButton extends Phaser.GameObjects.Image {

    private defaultScale: { x: number, y: number };
    private swapCursor: boolean = false;

    constructor(scene: Phaser.Scene, x: number, y: number, textureAtlas: string, frameNameOff: string, frameNameOn: string = frameNameOff, cursorPath: string = "", clickSound: boolean = true, pixelPerfect: boolean = false) {

        super(scene, x, y, textureAtlas, frameNameOff);

        if (pixelPerfect) {
            this.setInteractive(this.scene.input.makePixelPerfect(20));
            this.setInteractive({ pixelPerfect: true });
        } else {
            this.setInteractive();
        }

        if (cursorPath !== "") {
            this.swapCursor = true;
        }

        if (frameNameOff !== frameNameOn) {
            this.on("pointerover", () => {

                if (this.swapCursor) {
                    this.input.cursor = (cursorPath);
                }

                this.setFrame(frameNameOn);


            }, this);

            this.on("pointerout", () => {

                if (this.swapCursor) {
                    this.input.cursor = ("default");
                }

                this.setFrame(frameNameOff);


            }, this);

            this.on("pointerodown", () => {
                this.setFrame(frameNameOff);
            }, this);

            this.on("pointerup", () => {

                if (clickSound) {
                    AudioManager.playSound("click_btn");
                }

                this.setFrame(frameNameOn);

            }, this);
        }
    }

    public setInflationTween(ratio: number, tween?: boolean, tweenData?: { ease: any, duration: number }): void {

        this.defaultScale = { x: this.scaleX, y: this.scaleY };

        if (tween) {
            if (!tweenData) {
                tweenData = { ease: Phaser.Math.Easing.Cubic.Out, duration: 200 };
            }
        }

        this.on("pointerover", () => {
            if (tween) {
                const tween = this.scene.tweens.create({
                    targets: [this],
                    scaleX: this.defaultScale.x * ratio,
                    scaleY: this.defaultScale.y * ratio,
                    ease: tweenData.ease,
                    duration: tweenData.duration,

                });
                tween.play(true);
            } else {
                this.setScale(this.defaultScale.x * ratio, this.defaultScale.x * ratio);
            }
        }, this);

        this.on("pointerout", () => {
            if (tween) {
                const tween = this.scene.tweens.create({
                    targets: [this],
                    scaleX: this.defaultScale.x,
                    scaleY: this.defaultScale.y,
                    ease: tweenData.ease,
                    duration: tweenData.duration,

                });
                tween.play(true);
            } else {
                this.setScale(this.defaultScale.x, this.defaultScale.x);
            }
        }, this);

        this.on("pointerodown", () => {
            this.setScale(this.defaultScale.x, this.defaultScale.x);
        }, this);

        this.on("pointerup", () => {
            this.setScale(this.defaultScale.x * ratio, this.defaultScale.x * ratio);
        }, this);
    }

    public onUp(f: Function, context: any): void {
        this.on("pointerup", f, context);
    }

    public onDown(f: Function, context: any): void {
        this.on("pointerdown", f, context);
    }

    public onOver(f: Function, context: any): void {
        this.on("pointerover", f, context);
    }

    public onOut(f: Function, context: any): void {
        this.on("pointerout", f, context);
    }
}

export class AudioButton extends Phaser.GameObjects.Container {

    public static PX: number = 275;
    public static PY: number = 20;

    private button: Phaser.GameObjects.Image;
    private buttonOff: Phaser.GameObjects.Image;

    private forceOut: boolean = true;

    constructor(scene: Phaser.Scene) {

        super(scene, null);

        this.buttonOff = new Phaser.GameObjects.Image(scene, 0, 0, "texture_atlas_1", "btn_music_off.png");
        this.buttonOff.setOrigin(1, 0);
        this.buttonOff.setInteractive();

        this.button = new Phaser.GameObjects.Image(scene, 0, 0, "texture_atlas_1", "btn_music_on.png");
        this.button.setOrigin(1, 0);
        this.button.setInteractive();

        if (this.scene.sys.game.device.os.desktop) {

            this.button.on("pointerover", function () {

                if (this.forceOut) {
                    this.button.setTexture("texture_atlas_1", "btn_music_on_over.png");
                }

            }, this);

            this.button.on("pointerout", function () {

                if (!GameVars.gameData.muted) {
                    this.forceOut = true;
                }

                this.button.setTexture("texture_atlas_1", "btn_music_on.png");

            }, this);

            this.button.on("pointerdown", function () {

                this.onAudioButtonClicked();

                this.forceOut = false;

            }, this);

            this.buttonOff.on("pointerover", function () {

                if (this.forceOut) {
                    this.buttonOff.setTexture("texture_atlas_1", "btn_music_off_over.png");
                }

            }, this);

            this.buttonOff.on("pointerout", function () {
                this.buttonOff.setTexture("texture_atlas_1", "btn_music_off.png");

                if (GameVars.gameData.muted) {
                    this.forceOut = true;
                }

            }, this);

            this.buttonOff.on("pointerdown", function () {

                this.onAudioButtonClicked();
                this.forceOut = false;

            }, this);

        } else {

            this.button.on("pointerdown", function () {
                this.button.setTexture("texture_atlas_1", "btn_music_on_over.png");
                this.forceOut = false;
            }, this);

            this.buttonOff.on("pointerdown", function () {
                this.buttonOff.setTexture("texture_atlas_1", "btn_music_off_over.png");
                this.forceOut = false;

            }, this);

            this.button.on("pointerup", function () {
                this.onAudioButtonClicked();
                this.button.setTexture("texture_atlas_1", "btn_music_on.png");


            }, this);

            this.buttonOff.on("pointerup", function () {
                this.onAudioButtonClicked();
                this.buttonOff.setTexture("texture_atlas_1", "btn_music_off.png");

            }, this);
        }

        if (GameVars.gameData.muted) {
            this.button.visible = false;
            this.buttonOff.visible = true;
        } else {
            this.button.visible = true;
            this.buttonOff.visible = false;
        }

        this.add(this.button);
        this.add(this.buttonOff);
    }

    private onAudioButtonClicked(): void {

        AudioManager.toggleAudioState();

        if (GameVars.gameData.muted) {
            this.button.visible = false;
            this.buttonOff.visible = true;
            this.button.setTexture("texture_atlas_1", "btn_music_on.png");

        } else {
            this.button.visible = true;
            this.buttonOff.visible = false;
            this.buttonOff.setTexture("texture_atlas_1", "btn_music_off.png");
        }
    }
}



export class VanisheCircle extends Phaser.GameObjects.Container { // Array of dots with a current one highligted, commonly used for pagins indication  

    public scene: Phaser.Scene;
    public graphicColor: Phaser.Display.Color = null;

    private vanishTween: Phaser.Tweens.Tween = null;
    private repeat: number = 0;
    private goToVanish: Phaser.GameObjects.Graphics = null;
    private timeToVanish: number = 0;
    private finalScale: number = 0;
    private duration: number = 0;
    private finished: boolean = false;
    private started: boolean = false;

    constructor(scene: Phaser.Scene, x: number, y: number, graphicColor: Phaser.Display.Color, timeToVanish: number, finalScale: number, repeat: number, beginPaused: boolean) {

        super(scene, x, y);

        this.scene = scene;

        this.goToVanish = new Phaser.GameObjects.Graphics(this.scene);
        this.graphicColor = graphicColor;
        this.goToVanish.fillStyle(graphicColor.color, 1.0);
        this.goToVanish.fillCircle(0, 0, 0.1);

        this.scaleX = GameVars.scaleXMult;
        this.scaleY = GameVars.scaleYMult;

        this.add(this.goToVanish);

        this.timeToVanish = timeToVanish;

        this.repeat = repeat;

        this.vanishTween = this.scene.tweens.add({
            targets: this.goToVanish,
            scaleX: finalScale,
            scaleY: finalScale,
            duration: 250,
            alpha: 0,
            ease: "EaseInOut",
            paused: true
        });

        this.vanishTween.pause();
    }

    public update(): void {

        this.goToVanish.setAlpha(1 - this.vanishTween.progress);

        if (this.vanishTween.progress === 1) {
            this.goToVanish.visible = false;
            this.goToVanish.setAlpha(1.0);
        }
    }

    public isPlaying(): boolean {
        return this.started;
    }

    public swapGraphicColor(newColor: Phaser.Display.Color) {

        this.goToVanish.clear();
        this.goToVanish.fillStyle(newColor.color, this.goToVanish.alpha);
        this.goToVanish.fillCircle(0, 0, 0.1);
    }

    public setColor(color: Phaser.Display.Color) {
        this.goToVanish.clear();
        this.graphicColor = color;
        this.goToVanish.fillStyle(color.color, 1.0);
        this.goToVanish.fillCircle(0, 0, 0.1);
    }

    public begin(): void {
        this.goToVanish.visible = true;
        this.vanishTween.restart();
    }
}

